import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../../layout/AppLayout";
import { Alert, ErpSelect, ErpTable, toOptions, today } from "../../components/erp";
import { errorText } from "../../api/client";
import { usePurchaseDropdowns, paymentApi, PendingPaymentRow } from "./api";

const BILLER_TYPES = [{ value: "0", label: "Supplier" }, { value: "1", label: "Agent" }];
const PAY_MODES = [{ value: "C", label: "Cash" }, { value: "B", label: "Bank" }, { value: "J", label: "Journal" }];

export default function Payments() {
  const dd = usePurchaseDropdowns();
  const qc = useQueryClient();
  const [billerType, setBillerType] = useState("0"); const [date, setDate] = useState(today()); const [psId, setPsId] = useState("");
  const [q, setQ] = useState<{ BillDate: string; BillerType: string; PsId?: string } | null>(null);
  const list = useQuery({ queryKey: ["purchase", "payments-pending", q], enabled: !!q, queryFn: () => paymentApi.pending(q!) });

  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [payMode, setPayMode] = useState("C"); const [checkNo, setCheckNo] = useState(""); const [remarks, setRemarks] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null); const [paying, setPaying] = useState(false);

  const rows = list.data?.Data || [];
  const selectedRows = rows.filter((r) => selected[r.RmArrivalID]);
  const totalAmount = selectedRows.reduce((s, r) => s + r.BalanceAmount, 0);

  const pay = async () => {
    if (!selectedRows.length || !q) return;
    setPaying(true); setMsg(null);
    try {
      const centerId = psId || "1";
      const v = await paymentApi.voucherNumber(date, centerId, payMode);
      if (v.Error) { setMsg({ ok: false, text: v.Error }); return; }
      const r = await paymentApi.post({
        PaymentDate: date, PaymentMode: payMode, Vocher: v.Data!.VoucherNo, AutoVoucherNo: v.Data!.AutoVoucherNo, CheckNo: checkNo, Remarks: remarks,
        Amount: totalAmount, BillerType: q.BillerType, CenterID: centerId,
        PaymentBreakUp: selectedRows.map((row) => ({ ArrivalID: row.RmArrivalID, Amount: row.BalanceAmount, BillerType: q.BillerType })),
      });
      if (r.Error) { setMsg({ ok: false, text: r.Error }); } else { setMsg({ ok: true, text: r.Msg || "Payment Saved Successfully" }); setSelected({}); qc.invalidateQueries({ queryKey: ["purchase", "payments-pending", q] }); }
    } catch (ex) { setMsg({ ok: false, text: errorText(ex) }); } finally { setPaying(false); }
  };

  return (
    <>
      <PageHeader title="RM Payments" location="Purchase" />
      <div className="row rowFilter"><div className="col-md-12"><div className="card"><div className="card-body"><div className="row">
        <div className="col col-md-3 mb-3"><ErpSelect id="drpPayPurchase" placeholder="Select Purchase Center" value={psId} onChange={setPsId} options={toOptions(dd.data?.PurchaseCenters, (r) => r.PurchaseStationID, (r) => r.StationName)} /></div>
        <div className="col col-md-3 mb-3"><ErpSelect id="drpBillerType" placeholder="Biller Type" value={billerType} onChange={setBillerType} options={BILLER_TYPES} /></div>
        <div className="col col-md-2 mb-3"><input type="date" className="form-control" max={today()} value={date} onChange={(e) => setDate(e.target.value)} data-testid="filter-pay-date" /></div>
        <div className="col col-md-2 mb-3"><button type="button" className="btn btn-primary" onClick={() => { setQ({ BillDate: date, BillerType: billerType, PsId: psId || undefined }); setSelected({}); }} data-testid="btn-pay-search">Search</button></div>
      </div></div></div></div></div>
      <div className="card"><div className="card-body">
        <ErpTable<PendingPaymentRow> rows={rows} loading={list.isLoading} error={list.isError ? errorText(list.error) : list.data?.Error || null} testId="payments-pending-table"
          columns={[
            { key: "sel", title: "", render: (r) => <input type="checkbox" checked={!!selected[r.RmArrivalID]} onChange={(e) => setSelected((s) => ({ ...s, [r.RmArrivalID]: e.target.checked }))} data-testid={`select-pay-${r.RmArrivalID}`} /> },
            { key: "SNO", title: "S.No" }, { key: "ArrivalNumber", title: "Arrival Number" }, { key: "ArrivalDate", title: "Arrival Date" }, { key: "SupplierName", title: "Supplier" },
            { key: "Agent", title: "Agent" }, { key: "BillAmt", title: "Bill Amount" }, { key: "PaidAmount", title: "Paid" }, { key: "BalanceAmount", title: "Balance", className: "text-end fw-bold" },
          ]} />
      </div></div>
      {selectedRows.length > 0 && (
        <div className="card"><div className="card-body">
          <h6>Pay Selected Bills — Total: <span data-testid="pay-total-amount">{totalAmount.toFixed(2)}</span></h6>
          <div className="row">
            <div className="col-md-3 mb-3"><label className="form-label">Payment Mode</label><ErpSelect id="drpPayMode" value={payMode} onChange={setPayMode} options={PAY_MODES} testId="pay-mode" /></div>
            <div className="col-md-3 mb-3"><label className="form-label">Cheque/Ref No</label><input className="form-control" value={checkNo} onChange={(e) => setCheckNo(e.target.value)} data-testid="pay-check-no" /></div>
            <div className="col-md-4 mb-3"><label className="form-label">Remarks</label><input className="form-control" value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="pay-remarks" /></div>
          </div>
          {msg && <Alert kind={msg.ok ? "success" : "danger"} testId="pay-msg">{msg.text}</Alert>}
          <button type="button" className="btn btn-success" disabled={paying} onClick={pay} data-testid="pay-submit-btn">{paying ? "Processing…" : `Pay ${selectedRows.length} Bill(s)`}</button>
        </div></div>
      )}
    </>
  );
}
